import { browserPresetWorkspace } from "../js/presets/browser-workspace.js";
import { browserFishydexCurrentRepository } from "../js/fishydex/current-repository.js";
import {
  createMapPresetPayloadFromProjection,
  defaultMapPresetPayload,
  loadSharedFishRestoreState,
  mapPresetPayloadsEqual,
  mapPresetRestorePatch,
  normalizeMapPresetPayload,
} from "./map-page-state.js";
import { assertMapShellProjectionV1 } from "./map-shell-projection.js";

export const MAP_PRESET_COLLECTION_KEY = "map-presets";

let actionSequence = 0;

function cloneJson(value) {
  return JSON.parse(JSON.stringify(value));
}

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function requireWorkspace(workspace) {
  const candidate = workspace ?? browserPresetWorkspace();
  for (const method of [
    "bindCollectionRuntime",
    "edit",
    "initialize",
    "load",
    "state",
    "subscribe",
  ]) {
    if (typeof candidate?.[method] !== "function") {
      throw new TypeError(`Preset workspace is missing ${method}()`);
    }
  }
  return candidate;
}

function requireFishydexCurrent(repository) {
  const candidate = repository ?? browserFishydexCurrentRepository();
  for (const method of ["load", "state", "subscribe"]) {
    if (typeof candidate?.[method] !== "function") {
      throw new TypeError(`Fishydex Current repository is missing ${method}()`);
    }
  }
  return candidate;
}

function nextActionId(prefix) {
  const uuid = globalThis.crypto?.randomUUID?.();
  if (typeof uuid === "string" && uuid) {
    return `${prefix}.${uuid}`;
  }
  actionSequence += 1;
  return `${prefix}.${Date.now().toString(36)}.${actionSequence.toString(36)}`;
}

function viewFromBridgeState(readBridgeState) {
  if (typeof readBridgeState !== "function") {
    return null;
  }
  const snapshot = readBridgeState();
  return isPlainObject(snapshot?.view) ? cloneJson(snapshot.view) : null;
}

export function captureMapPresetCurrent(
  readProjection,
  readBridgeState,
  options = {},
) {
  if (typeof readProjection !== "function") {
    throw new TypeError("Map preset capture requires a projection reader");
  }
  const projection = assertMapShellProjectionV1(readProjection());
  const intent = String(options?.intent ?? "").trim();
  const includeCamera = intent === "save" || intent === "clone";
  if (!includeCamera) {
    return createMapPresetPayloadFromProjection(projection, {
      includeCamera: false,
    });
  }
  const bridgeView = viewFromBridgeState(readBridgeState);
  return createMapPresetPayloadFromProjection(projection, {
    includeCamera: true,
    capturedView: bridgeView,
  });
}

export function bindMapPresetWorkspaceRuntime({
  workspace,
  readProjection,
  applyPatch = () => {},
  readBridgeState = () => null,
} = {}) {
  const owner = requireWorkspace(workspace);
  if (typeof readProjection !== "function") {
    throw new TypeError("Map preset runtime requires a projection reader");
  }
  let applyingDepth = 0;
  let lastAppliedCommandRevision = null;

  const applyCurrent = async (payload, context = {}) => {
    const canonical = normalizeMapPresetPayload(payload);
    const commandRevision = context?.commandRevision ?? null;
    if (commandRevision !== null && commandRevision === lastAppliedCommandRevision) {
      return canonical;
    }
    if (
      context?.cause === "load"
      && Object.keys(canonical.view.camera).length === 0
      && mapPresetPayloadsEqual(
        canonical,
        captureMapPresetCurrent(readProjection, readBridgeState),
      )
    ) {
      lastAppliedCommandRevision = commandRevision;
      return canonical;
    }
    applyingDepth += 1;
    try {
      await applyPatch(mapPresetRestorePatch(canonical));
      lastAppliedCommandRevision = commandRevision;
      return canonical;
    } finally {
      applyingDepth -= 1;
    }
  };

  const disposeRuntime = owner.bindCollectionRuntime(MAP_PRESET_COLLECTION_KEY, {
    captureCurrent(options = {}) {
      return captureMapPresetCurrent(readProjection, readBridgeState, options);
    },
    applyCurrent,
  });

  return {
    applyCurrent,
    dispose() {
      disposeRuntime?.();
    },
    isApplying() {
      return applyingDepth > 0;
    },
    lastAppliedCommandRevision() {
      return lastAppliedCommandRevision;
    },
  };
}

function reportControllerError(onError, error) {
  if (typeof onError === "function") {
    onError(error);
    return;
  }
  globalThis.console?.error?.("Map preset workspace operation failed", error);
}

function reportControllerErrorSafely(onError, error) {
  try {
    reportControllerError(onError, error);
  } catch (_reportError) {
    // Projection subscribers must not interrupt later owner subscribers.
  }
}

export function bindMapPresetController({
  workspace = null,
  readProjection,
  subscribeProjection,
  applyPatch = () => {},
  readBridgeState = () => null,
  onError = null,
} = {}) {
  const owner = requireWorkspace(workspace);
  if (typeof readProjection !== "function") {
    throw new TypeError("Map preset controller requires a projection reader");
  }
  if (typeof subscribeProjection !== "function") {
    throw new TypeError("Map preset controller requires a projection subscription");
  }
  let disposed = false;
  let currentState = null;
  let bootComplete = false;
  let editEpoch = 0;
  let pendingEdit = null;
  let editLoop = null;
  let interactionActionId = "";
  let observedUi = null;
  let observedBridged = null;
  let observedPayload = null;
  let receivedInitialProjection = false;
  let unsubscribeProjection = null;
  let unsubscribeWorkspace = null;
  let ready = null;

  const invalidateCapturedEdits = () => {
    editEpoch += 1;
    pendingEdit = null;
    interactionActionId = "";
  };
  const runtime = bindMapPresetWorkspaceRuntime({
    workspace: owner,
    readProjection,
    applyPatch(patch) {
      invalidateCapturedEdits();
      return applyPatch(patch);
    },
    readBridgeState,
  });

  const boot = async () => {
    await owner.load(MAP_PRESET_COLLECTION_KEY);
    if (disposed) {
      return currentState;
    }
    currentState = owner.state(MAP_PRESET_COLLECTION_KEY);
    if (currentState?.kind === "missing") {
      const payload = captureMapPresetCurrent(readProjection, readBridgeState);
      const initialized = await owner.initialize(MAP_PRESET_COLLECTION_KEY, payload, {
        source: { kind: "fixed", id: "default" },
        expectedRevision: currentState.revision,
        initialEdit: {
          actionId: nextActionId("map-preset-bootstrap"),
          actionKind: "payload-change",
        },
      });
      currentState = initialized?.kind === "conflict"
        ? initialized.authoritative
        : initialized;
    } else if (
      currentState?.kind === "ready"
      && currentState.current?.payload
      && runtime.lastAppliedCommandRevision() !== currentState.revision
    ) {
      await runtime.applyCurrent(currentState.current.payload, {
        cause: "load",
        commandRevision: currentState.revision,
      });
    }
    bootComplete = true;
    return currentState;
  };

  const startEditLoop = () => {
    if (disposed || editLoop || !pendingEdit) {
      return editLoop;
    }
    interactionActionId ||= nextActionId("map-preset-change");
    editLoop = (async () => {
      if (!bootComplete) {
        await ready;
      }
      while (!disposed && pendingEdit) {
        const edit = pendingEdit;
        pendingEdit = null;
        if (edit.epoch !== editEpoch) {
          continue;
        }
        const state = owner.state(MAP_PRESET_COLLECTION_KEY);
        if (state?.kind !== "ready") {
          break;
        }
        if (
          state.current?.payload
          && mapPresetPayloadsEqual(state.current.payload, edit.payload)
        ) {
          currentState = state;
          continue;
        }
        interactionActionId ||= nextActionId("map-preset-change");
        const result = await owner.edit(MAP_PRESET_COLLECTION_KEY, edit.payload, {
          expectedRevision: state.revision,
          actionId: interactionActionId,
          actionKind: "payload-change",
        });
        if (edit.epoch !== editEpoch) {
          currentState = owner.state(MAP_PRESET_COLLECTION_KEY);
          continue;
        }
        currentState = result;
        if (result?.kind !== "ready") {
          pendingEdit = null;
          break;
        }
      }
    })().catch((error) => {
      pendingEdit = null;
      reportControllerErrorSafely(onError, error);
    }).finally(() => {
      editLoop = null;
      interactionActionId = "";
      if (pendingEdit) {
        startEditLoop();
      }
    });
    return editLoop;
  };

  const queueCurrent = (payload) => {
    if (disposed || runtime.isApplying()) {
      return null;
    }
    pendingEdit = { epoch: editEpoch, payload };
    startEditLoop();
    return payload;
  };

  const trackCurrent = () => {
    if (disposed || runtime.isApplying()) {
      return null;
    }
    return queueCurrent(captureMapPresetCurrent(readProjection, readBridgeState));
  };

  const observeProjection = (projection) => {
    const current = assertMapShellProjectionV1(projection);
    if (
      observedPayload !== null
      && current.ui === observedUi
      && current.bridged === observedBridged
    ) {
      return false;
    }
    const payload = createMapPresetPayloadFromProjection(current, {
      includeCamera: false,
    });
    receivedInitialProjection = true;
    if (observedPayload === null) {
      observedUi = current.ui;
      observedBridged = current.bridged;
      observedPayload = payload;
      return false;
    }
    observedUi = current.ui;
    observedBridged = current.bridged;
    if (mapPresetPayloadsEqual(payload, observedPayload)) {
      observedPayload = payload;
      return false;
    }
    observedPayload = payload;
    return Boolean(queueCurrent(payload));
  };

  const handleProjection = (projection) => {
    if (disposed) {
      return false;
    }
    try {
      return observeProjection(projection);
    } catch (error) {
      reportControllerErrorSafely(onError, error);
      return false;
    }
  };

  const cleanup = () => {
    const stopProjection = unsubscribeProjection;
    const stopWorkspace = unsubscribeWorkspace;
    unsubscribeProjection = null;
    unsubscribeWorkspace = null;
    for (const dispose of [stopProjection, stopWorkspace, () => runtime.dispose()]) {
      try {
        dispose?.();
      } catch (error) {
        reportControllerErrorSafely(onError, error);
      }
    }
  };

  let resolveReady;
  let rejectReady;
  ready = new Promise((resolve, reject) => {
    resolveReady = resolve;
    rejectReady = reject;
  });
  try {
    unsubscribeWorkspace = owner.subscribe(
      MAP_PRESET_COLLECTION_KEY,
      (nextState) => {
        currentState = nextState;
      },
    );
    if (typeof unsubscribeWorkspace !== "function") {
      throw new TypeError(
        "Map preset workspace subscription must return an unsubscribe function",
      );
    }
    unsubscribeProjection = subscribeProjection(handleProjection, {
      emitCurrent: true,
    });
    if (typeof unsubscribeProjection !== "function") {
      throw new TypeError(
        "Map preset projection subscription must return an unsubscribe function",
      );
    }
    if (!receivedInitialProjection) {
      throw new TypeError(
        "Map preset projection subscription must deliver its current projection",
      );
    }
  } catch (error) {
    disposed = true;
    cleanup();
    throw error;
  }
  void boot().then(resolveReady, (error) => {
    disposed = true;
    pendingEdit = null;
    cleanup();
    reportControllerErrorSafely(onError, error);
    rejectReady(error);
  });

  const controller = {
    ready,
    state() {
      return currentState ?? owner.state(MAP_PRESET_COLLECTION_KEY);
    },
    trackCurrent,
    async flush() {
      await ready;
      while (editLoop) {
        await editLoop;
      }
      return owner.state(MAP_PRESET_COLLECTION_KEY);
    },
    dispose() {
      if (disposed) {
        return;
      }
      disposed = true;
      pendingEdit = null;
      cleanup();
    },
  };
  return controller;
}

export function bindFishydexCurrentMapProjection({
  repository = null,
  readFishCatalog = () => [],
  sharedFishState = null,
  applyPatch = () => {},
  onError = null,
} = {}) {
  const owner = requireFishydexCurrent(repository);
  let disposed = false;
  let currentState = null;
  let lastProjectedRevision = "";

  const project = (nextState, { force = false } = {}) => {
    currentState = nextState;
    if (disposed || nextState?.kind !== "ready") {
      return null;
    }
    const revision = String(nextState.revision ?? "");
    if (!force && revision && revision === lastProjectedRevision) {
      return null;
    }
    const patch = loadSharedFishRestoreState({
      fishydexCurrentState: nextState,
      fishCatalog: readFishCatalog(),
      sharedFishState,
    });
    if (!patch) {
      return null;
    }
    applyPatch(patch, {
      cause: "fishydex-current-projection",
      commandRevision: nextState.revision,
    });
    lastProjectedRevision = revision;
    return patch;
  };

  const unsubscribe = owner.subscribe((nextState) => {
    try {
      project(nextState);
    } catch (error) {
      reportControllerError(onError, error);
    }
  });
  const ready = owner.load().then(() => {
    const nextState = owner.state();
    project(nextState);
    return nextState;
  }).catch((error) => {
    reportControllerError(onError, error);
    throw error;
  });

  return {
    ready,
    state() {
      return currentState ?? owner.state();
    },
    refresh() {
      return project(owner.state(), { force: true });
    },
    dispose() {
      disposed = true;
      unsubscribe?.();
    },
  };
}

export { defaultMapPresetPayload };
